import { useState, useEffect } from 'react';
import { Card, DataTable, Badge, Text, Link, Tooltip, Box, InlineStack } from '@shopify/polaris';
import { CardHeading } from './cards/CardHeading';
import { getRequest, formatToCurrency } from './functions.jsx';

/*
  Bank transactions matched to this invoice or credit note (via the Ponto banking
  integration). Renders nothing when there are no matched transactions, or for users
  without banking access (the API is restricted to full administrators and returns 403).

  type: 'invoice_out' (faktuur), 'invoice_in' (Faktuurin), 'creditnote_out' (Creditnota) or 'creditnote_in' (CreditnotaIN)
  invoiceId: faktuurnr / faktuurinvolgnummer / CreditnotaID / CreditnotaINvolgnummer
*/
export default function InvoiceBankTransactions({ type, invoiceId }) {
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;
    (async () => {
      const data = await getRequest(`/api/ponto/invoice-transactions/${type}/${invoiceId}`);
      if (!cancelled && Array.isArray(data)) setTransactions(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [type, invoiceId]);

  if (transactions.length === 0) return null;

  const formatDate = (value) => (value ? new Date(value).toLocaleDateString('nl-BE') : '—');

  const rows = transactions.map((tx) => [
    formatDate(tx.ValueDate),
    <Tooltip content={`${tx.RemittanceInformation || tx.Description || ''} · ${tx.CounterpartReference || ''}`}>
      <Text as="span">{tx.CounterpartName || tx.CounterpartReference || '—'}</Text>
    </Tooltip>,
    <Text as="span" fontWeight="medium" tone={tx.Amount >= 0 ? 'success' : 'critical'}>
      {formatToCurrency(tx.Amount)}
    </Text>,
    // AllocatedAmount = the slice booked on THIS invoice (differs from BookedAmount when one transaction paid several invoices)
    formatToCurrency(tx.AllocatedAmount ?? tx.BookedAmount ?? 0),
    <Badge tone={tx.MatchSource === 'auto' ? 'success' : 'info'}>{tx.MatchSource === 'auto' ? 'Automatic' : 'Manual'}</Badge>,
    <Tooltip content={tx.MatchReason || ''}>
      <Text as="span" tone="subdued" variant="bodySm">
        {(tx.MatchReason || '').slice(0, 40)}
      </Text>
    </Tooltip>
  ]);

  return (
    <Card padding="0">
      <Box padding="300" paddingBlockEnd="0">
        <InlineStack align="space-between" blockAlign="center">
          <CardHeading title="Bank transactions" />
          <Link removeUnderline url="/banking">
            Open Banking
          </Link>
        </InlineStack>
      </Box>
      <DataTable
        columnContentTypes={['text', 'text', 'numeric', 'numeric', 'text', 'text']}
        headings={['Date', 'Counterparty', 'Amount', 'Booked', 'Match', 'Reason']}
        rows={rows}
        increasedTableDensity
        verticalAlign="middle"
      />
    </Card>
  );
}
